import type { ProjectedMessage } from '@crisp/ai-agents-client';

/** Prefix for the marker links, matched by CSS and by the panel's click handler. */
export const CITATION_HREF_PREFIX = '#cite-';

// Leading spaces are swallowed so the marker hugs the word it belongs to. The
// model sometimes folds several into one, as `[^5, ^6]` or `[^5, 6]`.
const CITATION_MARKER = /[ \t]*\[\^\d+(?:\s*,\s*\^?\d+)*\]/g;

// Fenced blocks and inline code are left alone. The middle branch covers a block
// that is still streaming in and has no closing fence yet; it has to be tried
// before the inline-code branch, which would otherwise claim two of an opening
// fence's three backticks as an empty code span.
const CODE_SEGMENT = /(```[\s\S]*?```|```[\s\S]*$|`[^`\n]*`)/g;

/**
 * Rewrites the model's `[^n]` footnote markers as `[n](#cite-n)` links.
 *
 * The renderer is `marked`, which has no footnote support, so the raw markers
 * would otherwise reach the reader as literal `[^1]` text. Markers without a
 * matching citation are left as they are rather than turned into dead links.
 */
export function linkifyCitationMarkers(content: string, citationIds: Iterable<number>): string {
  const known = new Set(citationIds);
  if (known.size === 0) return content;

  return content
    .split(CODE_SEGMENT)
    .map((segment, index) =>
      // split() on a capturing group interleaves the delimiters at odd indices.
      index % 2 === 1
        ? segment
        : segment.replace(CITATION_MARKER, (marker) => {
            const ids = (marker.match(/\d+/g) || []).filter((id) => known.has(Number(id)));
            return ids.length > 0
              ? ids.map((id) => `[${id}](${CITATION_HREF_PREFIX}${id})`).join('')
              : marker;
          })
    )
    .join('');
}

export function toBotProjectedMessage(
  id: string,
  content: string,
  partial: boolean,
  timestamp = Date.now()
): ProjectedMessage {
  return {
    id,
    type: 'bot',
    timestamp,
    content,
    partial,
  };
}
